/**
 * Batch Service — Data access layer for the batch management system.
 *
 * Handles CRUD for:
 *   - batch_distribution (per-job batch generation config)
 *   - org_qa_config (per-org QA policy)
 *   - annotation_batch (batch records)
 *   - quality_log (quality entries per batch)
 *
 * This service is stateless — call functions with required parameters.
 * Returns raw snake_case data; use batchAdapter for camelCase transforms.
 */

import { supabase } from '../lib/supabase';
import { safeMutation } from '../utils/safeSupabase';
import { logAudit, AUDIT_ACTIONS } from './auditService';
import {
  BATCH_STATES,
  sumTotalItems,
  sumCreditedItems,
  sumBillableItems,
  sumAcceptedItems,
  sumRejectedItems,
  filterMainBatches,
} from '../utils/batchProgress';

// ─── Batch Distribution Config (per-job) ──────────────────

/**
 * Fetch batch distribution config for a job.
 * @param {string} jobId - UUID of the job
 * @returns {Object|null} config row or null if not configured
 */
export async function fetchBatchConfig(jobId) {
  if (!jobId) return null;

  const { data, error } = await supabase
    .from('batch_distribution')
    .select('*')
    .eq('job_id', jobId)
    .maybeSingle();

  if (error) {
    console.error('fetchBatchConfig error:', error);
  }

  return data || null;
}

/**
 * Create or update batch distribution config for a job.
 * Uses upsert on the UNIQUE(job_id) constraint.
 *
 * @param {string} jobId - UUID of the job
 * @param {string} orgId - UUID of the organization
 * @param {Object} config - distribution config fields
 * @param {string} [config.distribution_method] - 'even' | 'weighted_quality' | 'weighted_availability' | 'manual'
 * @param {number} [config.batch_size] - items per batch (default 500)
 * @param {number} [config.total_items] - total annotation items
 * @param {number} [config.overlap_percent] - 0-100, % of items for IAA
 * @param {number} [config.overlap_annotator_count] - how many annotators per overlap item (2-5)
 * @param {boolean} [config.auto_assign] - auto-assign batches to team
 * @param {boolean} [config.auto_qa] - auto-route to QA on submit
 * @param {boolean} [config.auto_reroute_revisions] - auto-send rejections back
 * @param {string} [config.environment] - 'production' | 'test'
 * @returns {Object|null} upserted row or null on error
 */
export async function upsertBatchConfig(jobId, orgId, config) {
  if (!jobId || !orgId) return null;

  const row = {
    job_id: jobId,
    org_id: orgId,
    ...config,
    updated_at: new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from('batch_distribution')
    .upsert(row, { onConflict: 'job_id' })
    .select()
    .single();

  if (error) {
    console.error('upsertBatchConfig error:', error);
    return null;
  }

  return data;
}

/**
 * Delete batch distribution config for a job (reset before regenerating).
 * @param {string} jobId - UUID of the job
 * @returns {boolean} true if deleted
 */
export async function deleteBatchConfig(jobId) {
  if (!jobId) return false;

  const { error } = await supabase
    .from('batch_distribution')
    .delete()
    .eq('job_id', jobId);

  if (error) {
    console.error('deleteBatchConfig error:', error);
    return false;
  }

  return true;
}

// ─── Org QA Config (per-org) ──────────────────────────────

/**
 * Fetch QA config for an organization.
 * @param {string} orgId - UUID of the organization
 * @returns {Object|null} config row or null if not configured
 */
export async function fetchOrgQaConfig(orgId) {
  if (!orgId) return null;

  const { data, error } = await supabase
    .from('org_qa_config')
    .select('*')
    .eq('org_id', orgId)
    .maybeSingle();

  if (error) {
    console.error('fetchOrgQaConfig error:', error);
  }

  return data || null;
}

/**
 * Create or update QA config for an organization.
 * Uses upsert on the UNIQUE(org_id) constraint.
 *
 * @param {string} orgId - UUID of the organization
 * @param {Object} config - QA config fields
 * @param {string} [config.qa_method] - 'peer_review' | 'dedicated_qa' | 'client_review' | 'gold_standard' | 'overlap_iaa' | 'mixed'
 * @param {number} [config.review_rate_percent] - 1-100
 * @param {string} [config.review_routing] - 'same_team' | 'separate_qa_team' | 'client' | 'auto_assign'
 * @param {string} [config.feedback_format] - 'per_annotation' | 'per_batch' | 'detailed_comments'
 * @param {string} [config.revision_policy] - 'route_to_same' | 'route_to_senior' | 'flag_for_spl'
 * @param {number} [config.max_revision_count] - 1-10
 * @param {number} [config.gold_standard_percent] - 0-100
 * @param {boolean} [config.overlap_enabled] - default overlap for new jobs
 * @param {number} [config.overlap_default_percent] - default overlap % for new jobs
 * @param {string} [config.environment] - 'production' | 'test'
 * @returns {Object|null} upserted row or null on error
 */
export async function upsertOrgQaConfig(orgId, config) {
  if (!orgId) return null;

  const row = {
    org_id: orgId,
    ...config,
    updated_at: new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from('org_qa_config')
    .upsert(row, { onConflict: 'org_id' })
    .select()
    .single();

  if (error) {
    console.error('upsertOrgQaConfig error:', error);
    return null;
  }

  return data;
}

// ─── Batch Generation ─────────────────────────────────────

/**
 * Delete all existing batches for a job (used before regenerating).
 * @param {string} jobId - UUID of the job
 * @returns {boolean} true if cleared
 */
export async function clearBatchesForJob(jobId) {
  if (!jobId) return false;

  // Capture pre-delete counts so the audit row records what was wiped.
  const { count: batchCount } = await supabase
    .from('annotation_batch')
    .select('*', { count: 'exact', head: true })
    .eq('job_id', jobId);
  const { count: qualityLogCount } = await supabase
    .from('quality_log')
    .select('*', { count: 'exact', head: true })
    .eq('job_id', jobId);

  // Delete dependent quality_log rows first (no ON DELETE CASCADE on batch_id FK)
  const { error: qlErr } = await supabase.from('quality_log').delete().eq('job_id', jobId);
  if (qlErr) console.warn('[batchService] Failed to clean quality_log:', qlErr.message);

  const { error } = await supabase
    .from('annotation_batch')
    .delete()
    .eq('job_id', jobId);

  if (error) {
    console.error('clearBatchesForJob error:', error);
    return false;
  }

  // Phase 11 slot 5: high-blast-radius destructive admin op — audit it.
  logAudit(AUDIT_ACTIONS.CLEAR_BATCHES_FOR_JOB, {
    entityType: 'job',
    entityId: jobId,
    details: {
      deletedBatchCount: batchCount || 0,
      deletedQualityLogCount: qualityLogCount || 0,
    },
  });

  return true;
}

/**
 * Allocate items per person for even distribution.
 * Spreads remainder across first N people (1 extra item each) so no one
 * gets more than 1 item above the base share.
 *
 * @param {number} totalItems - total items to distribute
 * @param {Array} teamMembers - array of { id, ... }
 * @returns {Array} [{ individualId, itemCount }]
 */
function allocateEven(totalItems, teamMembers) {
  if (!teamMembers.length) return [];

  const baseShare = Math.floor(totalItems / teamMembers.length);
  const remainder = totalItems % teamMembers.length;

  return teamMembers.map((member, i) => ({
    individualId: member.id,
    itemCount: baseShare + (i < remainder ? 1 : 0),
  }));
}

/**
 * Chunk one person's item allocation into batches of batchSize.
 * Last batch gets the remainder.
 *
 * @param {string} individualId - UUID (or null if unassigned)
 * @param {number} itemCount - total items for this person
 * @param {number} batchSize - items per batch
 * @param {number} startBatchNumber - batch numbering offset
 * @returns {Array} [{ individual_id, batch_number, total_items }]
 */
function chunkIntoBatches(individualId, itemCount, batchSize, startBatchNumber) {
  const batches = [];
  let remaining = itemCount;
  let batchNum = startBatchNumber;

  while (remaining > 0) {
    const size = Math.min(remaining, batchSize);
    batches.push({
      individual_id: individualId,
      batch_number: batchNum,
      total_items: size,
    });
    remaining -= size;
    batchNum++;
  }

  return batches;
}

/**
 * Generate batches for a job using even distribution.
 * Clears existing batches first (idempotent regeneration).
 *
 * @param {string} jobId - UUID of the job
 * @param {string} orgId - UUID of the organization
 * @param {Array} teamMembers - array of { id, name, ... } (active assignment members)
 * @param {Object} config - from batch_distribution table or defaults
 * @param {number} config.total_items - total annotation items
 * @param {number} [config.batch_size=500] - items per batch
 * @param {boolean} [config.auto_assign=true] - assign to team or leave unassigned
 * @param {string} [config.environment='production']
 * @returns {Object} { batches: Array, stats: { totalBatches, totalItems, perPerson } }
 */
export async function generateBatchesEven(jobId, orgId, teamMembers, config) {
  const {
    total_items,
    batch_size = 500,
    auto_assign = true,
    environment = 'production',
  } = config;

  if (!jobId || !orgId || !total_items || total_items <= 0) {
    return { batches: [], stats: null };
  }

  // Clear existing batches (idempotent)
  const cleared = await clearBatchesForJob(jobId);
  if (!cleared) return { batches: [], stats: null };

  let batchRows = [];

  if (!auto_assign || !teamMembers || teamMembers.length === 0) {
    // No team or manual mode — create unassigned batches
    batchRows = chunkIntoBatches(null, total_items, batch_size, 1);
  } else {
    // Even allocation across team
    const allocations = allocateEven(total_items, teamMembers);
    let batchNumber = 1;

    for (const alloc of allocations) {
      const personBatches = chunkIntoBatches(
        alloc.individualId,
        alloc.itemCount,
        batch_size,
        batchNumber
      );
      batchRows.push(...personBatches);
      batchNumber += personBatches.length;
    }
  }

  return insertAndReturnBatches(batchRows, jobId, orgId, environment);
}

/**
 * Shared insert logic — takes pre-computed batch rows, writes to DB, returns stats.
 * Used by all generation functions (even, weighted, manual).
 *
 * @param {Array} batchRows - [{ individual_id, batch_number, total_items }]
 * @param {string} jobId
 * @param {string} orgId
 * @param {string} environment
 * @returns {Object} { batches, stats }
 */
async function insertAndReturnBatches(batchRows, jobId, orgId, environment) {
  const now = new Date().toISOString();
  const insertRows = batchRows.map(b => ({
    org_id: orgId,
    job_id: jobId,
    individual_id: b.individual_id,
    batch_number: b.batch_number,
    total_items: b.total_items,
    status: BATCH_STATES.IN_PROGRESS, // All new batches start In Progress (individual_id may be null = unassigned)
    assigned_at: b.individual_id ? now : null,
    is_overlap: b.is_overlap || false,
    overlap_group_id: b.overlap_group_id || null,
    environment,
    created_at: now,
    updated_at: now,
  }));

  // Insert in chunks of 50 to avoid payload limits
  const CHUNK_SIZE = 50;
  const inserted = [];

  for (let i = 0; i < insertRows.length; i += CHUNK_SIZE) {
    const chunk = insertRows.slice(i, i + CHUNK_SIZE);
    const { data, error } = await supabase
      .from('annotation_batch')
      .insert(chunk)
      .select();

    if (error) {
      console.error(`insertAndReturnBatches error (chunk ${i / CHUNK_SIZE}):`, error);
      // Abort on failure — don't silently produce a half-generated result
      return {
        batches: inserted,
        stats: null,
        error: `Batch insert failed at chunk ${Math.floor(i / CHUNK_SIZE)}: ${error.message}`,
      };
    }

    if (data) inserted.push(...data);
  }

  // Compute stats
  const perPerson = {};
  for (const b of inserted) {
    const pid = b.individual_id || 'unassigned';
    if (!perPerson[pid]) perPerson[pid] = { batches: 0, items: 0 };
    perPerson[pid].batches++;
    perPerson[pid].items += b.total_items;
  }

  return {
    batches: inserted,
    stats: {
      totalBatches: inserted.length,
      totalItems: inserted.reduce((sum, b) => sum + b.total_items, 0),
      perPerson,
    },
  };
}

// ─── Weighted Distribution ────────────────────────────────

/**
 * Allocate items weighted by quality score.
 * Higher quality_score → proportionally larger share.
 * Falls back to even if all scores are equal or missing.
 *
 * @param {number} totalItems
 * @param {Array} teamMembers - [{ id, quality_score }]
 * @returns {Array} [{ individualId, itemCount }]
 */
function allocateByQuality(totalItems, teamMembers) {
  const scored = teamMembers.map(m => ({
    id: m.id,
    score: m.quality_score || m.qualityScore || 0,
  }));

  const totalScore = scored.reduce((sum, m) => sum + m.score, 0);

  // If no scores or all zero, fall back to even
  if (totalScore === 0) return allocateEven(totalItems, teamMembers);

  // Proportional allocation
  let allocated = 0;
  const allocations = scored.map((m, i) => {
    const isLast = i === scored.length - 1;
    const share = isLast
      ? totalItems - allocated  // last person gets remainder (no rounding loss)
      : Math.round((m.score / totalScore) * totalItems);
    allocated += share;
    return { individualId: m.id, itemCount: Math.max(0, share) };
  });

  return allocations;
}

/**
 * Allocate items weighted by available capacity.
 * More available hours → proportionally larger share.
 * Caller provides `available_capacity` (or `availableCapacity`) on each member.
 * Falls back to even if all capacities are equal or missing.
 *
 * @param {number} totalItems
 * @param {Array} teamMembers - [{ id, available_capacity | availableCapacity }]
 * @returns {Array} [{ individualId, itemCount }]
 */
function allocateByAvailability(totalItems, teamMembers) {
  const capacities = teamMembers.map(m => ({
    id: m.id,
    capacity: m.available_capacity || m.availableCapacity || 0,
  }));

  const totalCapacity = capacities.reduce((sum, m) => sum + m.capacity, 0);

  // If no capacity data or all zero, fall back to even
  if (totalCapacity === 0) return allocateEven(totalItems, teamMembers);

  // Skip anyone with 0 capacity
  const active = capacities.filter(m => m.capacity > 0);
  const activeTotal = active.reduce((sum, m) => sum + m.capacity, 0);
  if (activeTotal === 0 || active.length === 0) return allocateEven(totalItems, teamMembers);

  let allocated = 0;
  const allocations = active.map((m, i) => {
    const isLast = i === active.length - 1;
    const share = isLast
      ? totalItems - allocated
      : Math.round((m.capacity / activeTotal) * totalItems);
    allocated += share;
    return { individualId: m.id, itemCount: Math.max(0, share) };
  });

  return allocations;
}

/**
 * Generate batches using weighted distribution (quality or availability).
 * Clears existing batches first (idempotent regeneration).
 *
 * @param {string} jobId
 * @param {string} orgId
 * @param {Array} teamMembers - enriched with quality_score and/or available_capacity
 * @param {Object} config - from batch_distribution table
 * @param {string} config.distribution_method - 'weighted_quality' | 'weighted_availability'
 * @param {number} config.total_items
 * @param {number} [config.batch_size=500]
 * @param {string} [config.environment='production']
 * @returns {Object} { batches, stats }
 */
export async function generateBatchesWeighted(jobId, orgId, teamMembers, config) {
  const {
    distribution_method,
    total_items,
    batch_size = 500,
    environment = 'production',
  } = config;

  if (!jobId || !orgId || !total_items || total_items <= 0) {
    return { batches: [], stats: null };
  }

  if (!teamMembers || teamMembers.length === 0) {
    return { batches: [], stats: null };
  }

  const cleared = await clearBatchesForJob(jobId);
  if (!cleared) return { batches: [], stats: null };

  // Pick allocation strategy
  const allocations = distribution_method === 'weighted_availability'
    ? allocateByAvailability(total_items, teamMembers)
    : allocateByQuality(total_items, teamMembers);

  // Chunk each person's allocation into batches
  let batchRows = [];
  let batchNumber = 1;

  for (const alloc of allocations) {
    if (alloc.itemCount <= 0) continue;
    const personBatches = chunkIntoBatches(
      alloc.individualId,
      alloc.itemCount,
      batch_size,
      batchNumber
    );
    batchRows.push(...personBatches);
    batchNumber += personBatches.length;
  }

  return insertAndReturnBatches(batchRows, jobId, orgId, environment);
}

// ─── Overlap / IAA Batch Generation ───────────────────────

/**
 * Generate a UUID v4 for overlap_group_id linking.
 *
 * Phase 11 slot 8 (2026-04-24): replaced Math.random()-based fallback with
 * crypto.randomUUID(). The previous implementation labeled itself "without
 * crypto dependency" but crypto.randomUUID is available in all browsers
 * Juuni AI targets (and Node 14.17+, which the SSR/test environment uses).
 * Math.random is not a CSPRNG — using it for IDs creates a (small)
 * collision risk and conflates "non-token use" patterns with "tokens"
 * for static-scan tools that flag Math.random for security.
 */
function simpleUUID() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Last-resort fallback only — should never hit on supported targets.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Generate round-robin annotator pairs for overlap comparison.
 * Spreads overlap evenly so every annotator gets compared, not always the same pair.
 *
 * Example with [A,B,C,D] and count=2:
 *   Group 1: [A, B]
 *   Group 2: [C, D]
 *   Group 3: [A, C]
 *   Group 4: [B, D]
 *   ...repeats
 *
 * @param {Array} memberIds - array of individual UUIDs
 * @param {number} annotatorsPerGroup - how many annotators per overlap group (2-5)
 * @param {number} groupCount - how many overlap groups to create
 * @returns {Array} [[id1, id2], [id3, id4], ...] — each sub-array is one overlap group
 */
function generateOverlapPairs(memberIds, annotatorsPerGroup, groupCount) {
  if (memberIds.length < annotatorsPerGroup) return [];

  const pairs = [];
  let offset = 0;

  for (let g = 0; g < groupCount; g++) {
    const group = [];
    for (let a = 0; a < annotatorsPerGroup; a++) {
      const idx = (offset + a) % memberIds.length;
      group.push(memberIds[idx]);
    }
    pairs.push(group);
    // Advance offset to rotate through different combinations
    offset += annotatorsPerGroup;
    // If we've cycled through, shift by 1 to create new combos
    if (offset >= memberIds.length) {
      offset = (offset % memberIds.length) + 1;
    }
  }

  return pairs;
}

/**
 * Generate overlap batches for IAA (inter-annotator agreement).
 * Called after main batch generation — adds ADDITIONAL batches on top.
 *
 * Takes overlap_percent of total_items, splits into batch_size chunks,
 * and assigns each chunk to overlap_annotator_count different annotators.
 *
 * @param {string} jobId
 * @param {string} orgId
 * @param {Array} teamMembers - [{ id, ... }]
 * @param {Object} config
 * @param {number} config.total_items
 * @param {number} config.overlap_percent - 1-100
 * @param {number} [config.overlap_annotator_count=2]
 * @param {number} [config.batch_size=500]
 * @param {string} [config.environment='production']
 * @returns {Object} { batches, stats } — only the overlap batches
 */
async function generateOverlapBatches(jobId, orgId, teamMembers, config) {
  const {
    total_items,
    overlap_percent,
    overlap_annotator_count = 2,
    batch_size = 500,
    environment = 'production',
  } = config;

  if (!overlap_percent || overlap_percent <= 0) return { batches: [], stats: null };
  if (!teamMembers || teamMembers.length < overlap_annotator_count) return { batches: [], stats: null };

  // Calculate overlap items
  const overlapItems = Math.round((overlap_percent / 100) * total_items);
  if (overlapItems <= 0) return { batches: [], stats: null };

  // Split overlap items into chunks of batch_size
  const overlapChunks = [];
  let remaining = overlapItems;
  while (remaining > 0) {
    overlapChunks.push(Math.min(remaining, batch_size));
    remaining -= batch_size;
  }

  // Generate annotator groups for each overlap chunk
  const memberIds = teamMembers.map(m => m.id);
  const groups = generateOverlapPairs(memberIds, overlap_annotator_count, overlapChunks.length);

  // Build overlap batch rows
  // Batch numbers start at 1000 to separate from main batches
  const batchRows = [];
  let batchNumber = 1000;

  for (let g = 0; g < overlapChunks.length; g++) {
    const chunkSize = overlapChunks[g];
    const annotatorGroup = groups[g] || groups[g % groups.length];
    const groupId = simpleUUID();

    for (const annotatorId of annotatorGroup) {
      batchRows.push({
        individual_id: annotatorId,
        batch_number: batchNumber,
        total_items: chunkSize,
        is_overlap: true,
        overlap_group_id: groupId,
      });
      batchNumber++;
    }
  }

  return insertAndReturnBatches(batchRows, jobId, orgId, environment);
}

// ─── Unified Entry Point ──────────────────────────────────

/**
 * Unified entry point — generates main batches + overlap batches.
 * Routes to the correct distribution algorithm, then adds overlap if configured.
 *
 * @param {string} jobId
 * @param {string} orgId
 * @param {Array} teamMembers - enriched member objects
 * @param {Object} config - from batch_distribution table
 * @returns {Object} { batches, overlapBatches, stats, overlapStats }
 */
export async function generateBatches(jobId, orgId, teamMembers, config) {
  const method = config.distribution_method || 'even';

  // Generate main batches
  let mainResult;
  switch (method) {
    case 'weighted_quality':
    case 'weighted_availability':
      mainResult = await generateBatchesWeighted(jobId, orgId, teamMembers, config);
      break;
    case 'even':
    default:
      mainResult = await generateBatchesEven(jobId, orgId, teamMembers, config);
      break;
  }

  // Generate overlap batches if configured (additive — does NOT clear main batches)
  let overlapResult = { batches: [], stats: null };
  const overlapPercent = config.overlap_percent || 0;

  if (overlapPercent > 0 && teamMembers && teamMembers.length >= 2) {
    overlapResult = await generateOverlapBatches(jobId, orgId, teamMembers, config);
  }

  return {
    batches: mainResult.batches,
    overlapBatches: overlapResult.batches,
    stats: mainResult.stats,
    overlapStats: overlapResult.stats,
  };
}

// ─── Batch Status Management (State Machine) ─────────────
//
// Per _NORTH-STAR.md §5.2, the 4-state batch lifecycle is:
//
//   In Progress  →  In Review  →  Delivered  →  Accepted
//      ▲                  │                │
//      │                  │                │
//      └── rejection_note bounce ──────────┘
//
// Rejection/revision loops back to In Progress with a rejection_note (captured
// in metadata on updateBatchStatus). Unassigned batches are In Progress with
// individual_id = NULL. Review claim is tracked via review_claimed_by/_at
// fields, not a separate "In QA" state.
//
// Legacy 9-state strings (Unassigned, Assigned, Submitted, In QA, QA Passed,
// Revision Requested, Rejected) are collapsed by migration 201 and blocked
// by migration 202's CHECK constraint + transition trigger.

/**
 * Valid state transitions. Each key maps to the list of allowed next states.
 * Frozen to prevent runtime mutation.
 */
export const VALID_TRANSITIONS = Object.freeze({
  [BATCH_STATES.IN_PROGRESS]: [BATCH_STATES.IN_REVIEW, BATCH_STATES.DELIVERED],
  [BATCH_STATES.IN_REVIEW]:   [BATCH_STATES.DELIVERED, BATCH_STATES.IN_PROGRESS],
  [BATCH_STATES.DELIVERED]:   [BATCH_STATES.ACCEPTED, BATCH_STATES.IN_PROGRESS],
  [BATCH_STATES.ACCEPTED]:    [], // Terminal state
});

/**
 * Map target state → which timestamp column to stamp on entry.
 * Frozen. All four states have a dedicated timestamp field.
 */
export const STATUS_TIMESTAMP_MAP = Object.freeze({
  [BATCH_STATES.IN_PROGRESS]: 'started_at',
  [BATCH_STATES.IN_REVIEW]:   'submitted_at',
  [BATCH_STATES.DELIVERED]:   'delivered_at',
  [BATCH_STATES.ACCEPTED]:    'accepted_at',
});

/**
 * Pure-function state-machine validator. Returns { valid, allowed }.
 * Call before attempting any batch.status update.
 *
 * @param {string} fromStatus — current batch status
 * @param {string} toStatus — proposed new status
 * @returns {{ valid: boolean, allowed: string[] }}
 */
export function validateTransition(fromStatus, toStatus) {
  const allowed = VALID_TRANSITIONS[fromStatus] || [];
  return {
    valid: allowed.includes(toStatus),
    allowed,
  };
}

/**
 * Pure-function lookup for the timestamp column for a given target state.
 * Returns null if the state isn't in the 4-state map.
 *
 * @param {string} toStatus
 * @returns {string|null}
 */
export function getTimestampFieldForTransition(toStatus) {
  return STATUS_TIMESTAMP_MAP[toStatus] || null;
}

/**
 * Update a batch's status with state machine validation.
 * Sets the corresponding timestamp and increments revision_count if entering Revision Requested.
 *
 * @param {string} batchId - UUID of the batch
 * @param {string} newStatus - target status
 * @param {Object} [metadata] - additional fields to update
 * @param {number} [metadata.items_completed] - update item count
 * @param {string} [metadata.qa_reviewer_id] - who reviewed
 * @param {string} [metadata.qa_method_used] - QA method applied
 * @param {number} [metadata.qa_score] - QA score
 * @param {string} [metadata.revision_notes] - notes for revision
 * @param {string} [metadata.individual_id] - assign to annotator (Unassigned → Assigned)
 * @param {boolean} [override=false] - skip transition validation (god mode)
 * @returns {Object|null} updated batch row, or null on error
 */
export async function updateBatchStatus(batchId, newStatus, metadata = {}, override = false) {
  if (!batchId || !newStatus) return null;

  // Fetch current batch
  const { data: current, error: fetchError } = await supabase
    .from('annotation_batch')
    .select('*')
    .eq('id', batchId)
    .single();

  if (fetchError || !current) {
    console.error('updateBatchStatus: batch not found', fetchError);
    return null;
  }

  // Validate transition (unless override/god mode)
  if (!override) {
    const { valid, allowed } = validateTransition(current.status, newStatus);
    if (!valid) {
      console.error(`Invalid batch transition: ${current.status} → ${newStatus}. Allowed: ${allowed.join(', ') || '(terminal)'}`);
      return null;
    }
  }

  // Build update payload
  const now = new Date().toISOString();
  const updates = {
    status: newStatus,
    updated_at: now,
  };

  // Set timestamp for this transition
  const tsField = getTimestampFieldForTransition(newStatus);
  if (tsField) {
    updates[tsField] = now;
  }

  // Increment revision_count on bounce-back (In Review | Delivered → In Progress)
  // Per North Star §5.2: rejection loops back to In Progress with rejection_note.
  const isBounceBack = newStatus === BATCH_STATES.IN_PROGRESS && (
    current.status === BATCH_STATES.IN_REVIEW ||
    current.status === BATCH_STATES.DELIVERED
  );
  if (isBounceBack) {
    updates.revision_count = (current.revision_count || 0) + 1;
  }

  // Clear review claim when leaving In Review
  if (current.status === BATCH_STATES.IN_REVIEW && newStatus !== BATCH_STATES.IN_REVIEW) {
    updates.review_claimed_by = null;
    updates.review_claimed_at = null;
  }

  // Merge metadata fields
  const { override: _ignored, ...metaFields } = metadata;
  const ALLOWED_META = [
    'items_completed', 'items_accepted', 'items_rejected',
    'qa_reviewer_id', 'qa_method_used', 'qa_score',
    'revision_notes', 'individual_id',
    // Backbone additions (migration 201):
    'rejection_note', 'review_claimed_by', 'review_claimed_at',
  ];
  for (const key of ALLOWED_META) {
    if (metaFields[key] !== undefined) {
      updates[key] = metaFields[key];
    }
  }

  const { data, error } = await supabase
    .from('annotation_batch')
    .update(updates)
    .eq('id', batchId)
    .select()
    .single();

  if (error) {
    console.error('updateBatchStatus error:', error);
    return null;
  }

  // Phase 11 slot 5: this is the single funnel for the 4-state lifecycle
  // (approveAndDeliver / rejectWithNote / markAccepted / markRejectedByClient
  // / routeForReview / advanceIfComplete all route through here). One audit
  // here covers all of them.
  logAudit(AUDIT_ACTIONS.TRANSITION_BATCH_STATE, {
    entityType: 'annotation_batch',
    entityId: batchId,
    details: {
      fromStatus: current.status,
      toStatus: newStatus,
      revisionCount: updates.revision_count ?? current.revision_count ?? 0,
      isBounceBack,
      hasRejectionNote: typeof updates.rejection_note === 'string' && updates.rejection_note.length > 0,
      qaScore: updates.qa_score ?? null,
      qaMethodUsed: updates.qa_method_used ?? null,
      override: !!override,
    },
  });

  // ── Post-transition hooks (fire-and-forget) ──────────
  // Fire AFTER the status update succeeds. Imports are dynamic to avoid
  // circular dependencies.
  try {
    if (newStatus === BATCH_STATES.IN_REVIEW) {
      // Auto-route to reviewer based on org QA config (pulls from qaRoutingService).
      // Previously keyed off legacy 'Submitted' state; now keyed off In Review
      // which is the equivalent "ready for review" signal in the 4-state world.
      const { routeBatchToQA } = await import('./qaRoutingService');
      routeBatchToQA(batchId, current.org_id).catch(err =>
        console.error('Auto-routing failed:', err)
      );
    }
  } catch (hookErr) {
    console.error('Post-transition hook error:', hookErr);
  }

  return data;
}

/**
 * Assign (or reassign) a batch to an annotator.
 *
 * In the 4-state world, batches always start `In Progress` regardless of
 * annotator assignment (individual_id = NULL means unassigned). This function
 * just sets the individual_id — no status transition needed.
 *
 * @param {string} batchId
 * @param {string} individualId - annotator UUID
 * @returns {Object|null} updated batch
 */
export async function assignBatch(batchId, individualId) {
  if (!batchId || !individualId) return null;

  // Capture previous owner so the audit row shows the actual ownership flip.
  const { data: prev } = await supabase
    .from('annotation_batch')
    .select('individual_id')
    .eq('id', batchId)
    .maybeSingle();

  const { data, error } = await supabase
    .from('annotation_batch')
    .update({
      individual_id: individualId,
      assigned_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', batchId)
    .select()
    .single();
  if (error) {
    console.error('assignBatch error:', error);
    return null;
  }

  // Phase 11 slot 5: ownership flip on shared batch.
  logAudit(AUDIT_ACTIONS.ASSIGN_BATCH, {
    entityType: 'annotation_batch',
    entityId: batchId,
    details: {
      fromIndividualId: prev?.individual_id || null,
      toIndividualId: individualId,
    },
  });

  return data;
}

/**
 * Reassign a batch to a different annotator (revision handoff or spiral recovery).
 * Preserves revision history (quality_log stays linked to batch_id).
 * Resets progress — new annotator starts fresh on the items.
 *
 * @param {string} batchId
 * @param {string} newAnnotatorId - individual_id of the new assignee
 * @param {string} [reason] - why it was reassigned
 * @returns {Object|null} updated batch
 */
export async function reassignBatch(batchId, newAnnotatorId, reason) {
  if (!batchId || !newAnnotatorId) return null;

  // Capture pre-reassign progress so the audit row records what was reset.
  const { data: prev } = await supabase
    .from('annotation_batch')
    .select('individual_id, status, items_completed, items_accepted, items_rejected, revision_count')
    .eq('id', batchId)
    .maybeSingle();

  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('annotation_batch')
    .update({
      individual_id: newAnnotatorId,
      status: BATCH_STATES.IN_PROGRESS, // Fresh start on 4-state lifecycle
      items_completed: 0,
      items_accepted: 0,
      items_rejected: 0,
      assigned_at: now,
      started_at: null,
      submitted_at: null,
      qa_completed_at: null,
      qa_reviewer_id: null,
      qa_score: null,
      review_claimed_by: null,
      review_claimed_at: null,
      rejection_note: reason ? `Reassigned: ${reason}` : 'Batch reassigned to new annotator',
      updated_at: now,
      // Keep revision_count — it's part of the batch history
    })
    .eq('id', batchId)
    .select()
    .single();

  if (error) {
    console.error('reassignBatch error:', error);
    return null;
  }

  // Phase 11 slot 5: destructive — resets work-progress columns. Capture both
  // the ownership flip and the reset values for the audit trail.
  logAudit(AUDIT_ACTIONS.REASSIGN_BATCH, {
    entityType: 'annotation_batch',
    entityId: batchId,
    details: {
      fromIndividualId: prev?.individual_id || null,
      toIndividualId: newAnnotatorId,
      reason: reason || null,
      resetProgress: true,
      previousProgress: {
        status: prev?.status || null,
        itemsCompleted: prev?.items_completed || 0,
        itemsAccepted: prev?.items_accepted || 0,
        itemsRejected: prev?.items_rejected || 0,
      },
      revisionCount: prev?.revision_count || 0,
    },
  });

  return data;
}

/**
 * Assign a revision to an annotator (same person or different).
 * Unlike reassignBatch, this preserves progress and just routes the revision.
 *
 * @param {string} batchId
 * @param {Object} options - { assignTo, notes, source }
 * @returns {{ success: boolean, error?: string }}
 */
export async function assignRevision(batchId, { assignTo = null, notes = null, source = null } = {}) {
  if (!batchId) return { success: false, error: 'Missing batch ID' };

  const updates = {
    status: 'In Progress',
    revision_assigned_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
  if (assignTo) {
    updates.individual_id = assignTo;
    updates.revision_assigned_to = assignTo;
  }
  if (notes) updates.revision_notes = notes;
  if (source) updates.revision_source = source;

  const { error } = await safeMutation(
    supabase.from('annotation_batch').update(updates).eq('id', batchId),
    'batch-assignRevision'
  );

  if (error) return { success: false, error: error.message };

  // Phase 11 slot 5: revision routing on a shared batch.
  logAudit(AUDIT_ACTIONS.ASSIGN_REVISION, {
    entityType: 'annotation_batch',
    entityId: batchId,
    details: {
      assignTo: assignTo || null,
      hasNotes: !!notes,
      source: source || null,
    },
  });

  return { success: true };
}

/**
 * SPL claims a batch for review. Moves the batch to In Review (if not already
 * there) and stamps review_claimed_by + review_claimed_at.
 *
 * If the batch is already In Review (previously claimed by someone else or
 * auto-advanced), this just updates the claim without triggering a transition.
 *
 * @param {string} batchId
 * @param {string} reviewerId - individual UUID of the reviewer (SPL/QA)
 * @returns {Object|null} updated batch
 */
export async function routeForReview(batchId, reviewerId) {
  if (!batchId || !reviewerId) return null;
  const now = new Date().toISOString();

  const { data: current } = await supabase
    .from('annotation_batch')
    .select('status')
    .eq('id', batchId)
    .single();

  if (!current) return null;

  if (current.status === BATCH_STATES.IN_REVIEW) {
    // Already In Review — just update the claim.
    const { data, error } = await supabase
      .from('annotation_batch')
      .update({
        review_claimed_by: reviewerId,
        review_claimed_at: now,
        updated_at: now,
      })
      .eq('id', batchId)
      .select()
      .single();
    if (error) {
      console.error('routeForReview error:', error);
      return null;
    }
    return data;
  }

  // Transition In Progress → In Review with claim
  return updateBatchStatus(batchId, BATCH_STATES.IN_REVIEW, {
    review_claimed_by: reviewerId,
    review_claimed_at: now,
  });
}

/**
 * SPL approves a batch under review → Delivered. Optional QA metadata.
 *
 * @param {string} batchId
 * @param {Object} [options] - { qa_score, qa_method_used, qa_reviewer_id }
 * @returns {Object|null} updated batch
 */
export async function approveAndDeliver(batchId, { qa_score, qa_method_used, qa_reviewer_id } = {}) {
  const metadata = {};
  if (qa_score !== undefined) metadata.qa_score = qa_score;
  if (qa_method_used !== undefined) metadata.qa_method_used = qa_method_used;
  if (qa_reviewer_id !== undefined) metadata.qa_reviewer_id = qa_reviewer_id;
  return updateBatchStatus(batchId, BATCH_STATES.DELIVERED, metadata);
}

/**
 * SPL rejects a batch under review → bounces back to In Progress with note.
 * revision_count increments automatically in updateBatchStatus.
 *
 * @param {string} batchId
 * @param {string} note - rejection explanation (captured as rejection_note)
 * @returns {Object|null} updated batch
 */
export async function rejectWithNote(batchId, note) {
  if (!batchId) return null;
  return updateBatchStatus(batchId, BATCH_STATES.IN_PROGRESS, {
    rejection_note: note || 'Rejected — returned for rework (no note provided)',
  });
}

/**
 * Client accepts a delivered batch → Accepted (terminal).
 *
 * @param {string} batchId
 * @returns {Object|null} updated batch
 */
export async function markAccepted(batchId) {
  return updateBatchStatus(batchId, BATCH_STATES.ACCEPTED);
}

/**
 * Client rejects a delivered batch → bounces back to In Progress with note.
 *
 * @param {string} batchId
 * @param {string} note
 * @returns {Object|null} updated batch
 */
export async function markRejectedByClient(batchId, note) {
  if (!batchId) return null;
  return updateBatchStatus(batchId, BATCH_STATES.IN_PROGRESS, {
    rejection_note: note || 'Rejected by client — returned for rework (no note provided)',
  });
}

/**
 * Auto-advance a batch from In Progress to In Review when items_completed
 * meets or exceeds total_items. No-op otherwise. Called by updateBatchProgress
 * after an items_completed update.
 *
 * Annotators have no explicit "submit" verb (North Star §5.2, invariant #5);
 * this is the event-driven equivalent — work is "done" when all items are
 * marked completed.
 *
 * @param {string} batchId
 * @returns {Object|null} updated batch if advanced, null otherwise
 */
export async function advanceIfComplete(batchId) {
  if (!batchId) return null;
  const { data: batch } = await supabase
    .from('annotation_batch')
    .select('id, status, items_completed, total_items')
    .eq('id', batchId)
    .single();

  if (!batch) return null;
  if (batch.status !== BATCH_STATES.IN_PROGRESS) return null;
  if ((batch.items_completed || 0) < (batch.total_items || 0)) return null;

  return updateBatchStatus(batchId, BATCH_STATES.IN_REVIEW);
}

/**
 * Update batch progress (items_completed). If the update brings the batch to
 * total_items, auto-advance to In Review (event-driven, per North Star §5.2 —
 * annotators have no explicit submit verb).
 *
 * @param {string} batchId
 * @param {number} itemsCompleted
 * @returns {Object|null} updated batch (may now be In Review if auto-advanced)
 */
export async function updateBatchProgress(batchId, itemsCompleted) {
  if (!batchId) return null;

  const { data, error } = await supabase
    .from('annotation_batch')
    .update({
      items_completed: itemsCompleted,
      updated_at: new Date().toISOString(),
    })
    .eq('id', batchId)
    .select()
    .single();

  if (error) {
    console.error('updateBatchProgress error:', error);
    return null;
  }

  // Auto-advance if items_completed now reaches total_items (event-driven submit).
  // Non-blocking: fire and continue; downstream consumers can re-fetch if they care.
  try {
    const advanced = await advanceIfComplete(batchId);
    if (advanced) return advanced;
  } catch (advErr) {
    console.error('advanceIfComplete error (non-fatal):', advErr);
  }

  return data;
}

/**
 * Add a single batch to a job (ops-mode: manual batch creation).
 * Auto-computes next batch_number from existing batches.
 */
export async function addSingleBatch(jobId, orgId, { totalItems, individualId, environment }) {
  if (!jobId || !totalItems) return { data: null, error: { message: 'Job ID and total items required' } };

  // Get next batch number
  const { data: existing } = await supabase
    .from('annotation_batch')
    .select('batch_number')
    .eq('job_id', jobId)
    .order('batch_number', { ascending: false })
    .limit(1);

  const nextNumber = (existing?.[0]?.batch_number || 0) + 1;

  const { data, error } = await supabase
    .from('annotation_batch')
    .insert({
      job_id: jobId,
      org_id: orgId || null,
      batch_number: nextNumber,
      total_items: totalItems,
      items_completed: 0,
      individual_id: individualId || null,
      status: BATCH_STATES.IN_PROGRESS, // 4-state lifecycle — batches start In Progress regardless of assignment
      environment: environment || 'production',
    })
    .select('*, individual:individual_id(id, name, primary_domain)')
    .single();

  if (error) {
    console.error('addSingleBatch error:', error);
    return { data: null, error };
  }

  return { data, error: null };
}

/**
 * Create a rework batch from rejected items of a parent batch.
 * Assigns to the same annotator, resets parent's items_rejected.
 */
export async function createReworkBatch(parentBatchId, rejectedCount, orgId) {
  if (!parentBatchId || !rejectedCount) return { data: null, error: { message: 'Parent batch and rejected count required' } };

  // Get parent batch details
  const { data: parent } = await supabase
    .from('annotation_batch')
    .select('job_id, individual_id, environment, batch_number')
    .eq('id', parentBatchId)
    .single();

  if (!parent) return { data: null, error: { message: 'Parent batch not found' } };

  // Get next batch number
  const { data: existing } = await supabase
    .from('annotation_batch')
    .select('batch_number')
    .eq('job_id', parent.job_id)
    .order('batch_number', { ascending: false })
    .limit(1);

  const nextNumber = (existing?.[0]?.batch_number || 0) + 1;

  // Create rework batch
  const { data, error } = await supabase
    .from('annotation_batch')
    .insert({
      job_id: parent.job_id,
      org_id: orgId || null,
      batch_number: nextNumber,
      total_items: rejectedCount,
      items_completed: 0,
      individual_id: parent.individual_id,
      status: BATCH_STATES.IN_PROGRESS, // 4-state lifecycle
      environment: parent.environment || 'production',
      notes: `Rework from Batch #${parent.batch_number} (${rejectedCount} rejected items)`,
    })
    .select('*, individual:individual_id(id, name, primary_domain)')
    .single();

  if (error) {
    console.error('createReworkBatch error:', error);
    return { data: null, error };
  }

  // Reset rejected count on parent
  await supabase
    .from('annotation_batch')
    .update({ items_rejected: 0, updated_at: new Date().toISOString() })
    .eq('id', parentBatchId);

  // Phase 11 slot 5: rework batch creation + parent rejected-count reset
  // (two writes — single audit captures both).
  logAudit(AUDIT_ACTIONS.CREATE_REWORK_BATCH, {
    entityType: 'annotation_batch',
    entityId: data?.id || parentBatchId,
    details: {
      parentBatchId,
      reworkBatchId: data?.id || null,
      rejectedCount,
      jobId: parent.job_id,
    },
  });

  return { data, error: null };
}

// ─── Batch Query Operations ───────────────────────────────

/**
 * Fetch all batches for a job, joined with annotator info.
 * Used by admin dashboard batch progress table.
 *
 * @param {string} jobId
 * @returns {Array} batch rows with nested individual { id, name, primary_domain }
 */
export async function fetchBatchesForJob(jobId) {
  if (!jobId) return [];

  const { data, error } = await supabase
    .from('annotation_batch')
    .select('*, individual:individual_id(id, name, primary_domain, email)')
    .eq('job_id', jobId)
    .order('batch_number', { ascending: true });

  if (error) {
    console.error('fetchBatchesForJob error:', error);
    return [];
  }

  return data || [];
}

/**
 * Fetch all batches for a dataset, joined with annotator info.
 * Used by /admin/dataset/:id detail page (Phase 5.75 Stage A Commit 6).
 *
 * Scopes to `dataset_id` (not `job_id`) — a single job may contain multiple
 * datasets, and the detail page only wants the batches for the one being viewed.
 *
 * @param {string} datasetId
 * @returns {Array} batch rows with nested individual { id, name, primary_domain, email }
 */
export async function fetchBatchesForDataset(datasetId) {
  if (!datasetId) return [];

  const { data, error } = await supabase
    .from('annotation_batch')
    .select('*, individual:individual_id(id, name, primary_domain, email)')
    .eq('dataset_id', datasetId)
    .order('batch_number', { ascending: true });

  if (error) {
    console.error('fetchBatchesForDataset error:', error);
    return [];
  }

  return data || [];
}

/**
 * Fetch all batches for MULTIPLE datasets in a single query. Used by the
 * Phase 5.75 Stage B B-4 catalog table to enrich every row with its
 * projectDataset rollup (progress %, quality avg, batch count) without
 * falling into an N+1-query pattern.
 *
 * Callers typically group the result client-side by `dataset_id` before
 * invoking `projectDataset({ dataset, batches })` per row.
 *
 * @param {string[]} datasetIds
 * @returns {Promise<Array>} batch rows (same shape as fetchBatchesForDataset)
 */
export async function fetchBatchesForDatasetIds(datasetIds) {
  if (!Array.isArray(datasetIds) || datasetIds.length === 0) return [];

  const { data, error } = await supabase
    .from('annotation_batch')
    .select('*, individual:individual_id(id, name, primary_domain, email)')
    .in('dataset_id', datasetIds);

  if (error) {
    console.error('fetchBatchesForDatasetIds error:', error);
    return [];
  }
  return data || [];
}

/**
 * Fetch all batches assigned to an annotator, joined with job info.
 * Used by annotator portal "My Batches" view.
 *
 * @param {string} individualId
 * @returns {Array} batch rows with nested job { id, title, domain, status }
 */
export async function fetchBatchesForAnnotator(individualId) {
  if (!individualId) return [];

  const { data, error } = await supabase
    .from('annotation_batch')
    .select('*, job:job_id(id, title, domain, status)')
    .eq('individual_id', individualId)
    .order('updated_at', { ascending: false });

  if (error) {
    console.error('fetchBatchesForAnnotator error:', error);
    return [];
  }

  return data || [];
}

/**
 * Fetch batches for a job filtered by status.
 *
 * @param {string} jobId
 * @param {string|Array} status - single status string or array of statuses
 * @returns {Array} matching batch rows
 */
export async function fetchBatchesByStatus(jobId, status) {
  if (!jobId || !status) return [];

  let query = supabase
    .from('annotation_batch')
    .select('*, individual:individual_id(id, name, primary_domain)')
    .eq('job_id', jobId)
    .order('batch_number', { ascending: true });

  if (Array.isArray(status)) {
    query = query.in('status', status);
  } else {
    query = query.eq('status', status);
  }

  const { data, error } = await query;

  if (error) {
    console.error('fetchBatchesByStatus error:', error);
    return [];
  }

  return data || [];
}

/**
 * Fetch overlap batches for a job, grouped by overlap_group_id.
 * Used by IAA comparison view.
 *
 * @param {string} jobId
 * @returns {Object} { groups: { [groupId]: [batch, batch, ...] }, groupCount }
 */
export async function fetchOverlapGroups(jobId) {
  if (!jobId) return { groups: {}, groupCount: 0 };

  const { data, error } = await supabase
    .from('annotation_batch')
    .select('*, individual:individual_id(id, name, primary_domain)')
    .eq('job_id', jobId)
    .eq('is_overlap', true)
    .order('overlap_group_id')
    .order('batch_number', { ascending: true });

  if (error) {
    console.error('fetchOverlapGroups error:', error);
    return { groups: {}, groupCount: 0 };
  }

  // Group by overlap_group_id
  const groups = {};
  for (const batch of (data || [])) {
    const gid = batch.overlap_group_id;
    if (!gid) continue;
    if (!groups[gid]) groups[gid] = [];
    groups[gid].push(batch);
  }

  return { groups, groupCount: Object.keys(groups).length };
}

/**
 * Compute batch statistics from an array of batch records.
 * Pure function — no DB call. Call fetchBatchesForJob first.
 *
 * @param {Array} batches - array of batch rows
 * @returns {Object} stats
 */
export function computeBatchStats(batches) {
  if (!batches || batches.length === 0) {
    return {
      totalBatches: 0,
      totalItems: 0,
      itemsCompleted: 0,   // legacy: raw items_completed sum
      itemsCredited: 0,    // progress (credited per batchProgress.js)
      itemsBillable: 0,    // accrual revenue
      itemsAccepted: 0,    // cash-conservative (Accepted only)
      itemsRejected: 0,
      completionPercent: 0,
      byStatus: {},
      overlapBatches: 0,
    };
  }

  const mainBatches = filterMainBatches(batches);
  const overlapBatches = batches.filter(b => b && b.is_overlap);

  // Delegates item aggregation to batchProgress.js (single source of truth
  // per North Star §5.3). Legacy `itemsCompleted` (raw sum) is preserved for
  // back-compat with callers that haven't migrated to the semantic fields yet.
  const totalItems = sumTotalItems(mainBatches);
  const itemsCredited = sumCreditedItems(mainBatches);
  const itemsBillable = sumBillableItems(mainBatches);
  const itemsAccepted = sumAcceptedItems(mainBatches);
  const itemsRejected = sumRejectedItems(mainBatches);
  const itemsCompleted = mainBatches.reduce((s, b) => s + (Number(b?.items_completed) || 0), 0);

  // Count by status (across all batches, including overlap)
  const byStatus = {};
  for (const b of batches) {
    if (!b) continue;
    byStatus[b.status] = (byStatus[b.status] || 0) + 1;
  }

  return {
    totalBatches: mainBatches.length,
    totalItems,
    itemsCompleted,   // legacy
    itemsCredited,    // progress
    itemsBillable,    // accrual revenue
    itemsAccepted,    // cash-conservative
    itemsRejected,
    // completionPercent uses credited (excludes rejected) per North Star invariant.
    // Was itemsCompleted/totalItems — over-stated progress when rejections existed.
    completionPercent: totalItems > 0 ? Math.round((itemsCredited / totalItems) * 100) : 0,
    byStatus,
    overlapBatches: overlapBatches.length,
  };
}
